import type { ButtonAnchorProps } from '@leafwell/components';
import React from 'react';
import { useRouter } from 'next/router';
import { Button } from '@leafwell/components';
import Image from 'next/image';
import { useWordPressContext } from 'contexts/wordpress-provider';

type PaginationVariant = 'default' | 'minimal';

const PaginationButton: React.FC<
  Omit<ButtonAnchorProps, 'onClick' | 'variant'> & {
    dataLoading?: boolean;
    isActive?: boolean;
    variant?: PaginationVariant;
  }
> = ({
  isActive = false,
  disabled = false,
  variant = 'default',
  href,
  label,
}) => {
  const isMinimal = variant === 'minimal';
  let buttonClassNames: string;

  if (variant === 'minimal') {
    buttonClassNames = isActive
      ? 'pointer-events-none font-medium !border-transparent !text-black'
      : '!text-gray-400 hover:!text-white';
  } else {
    buttonClassNames = isActive
      ? 'pointer-events-none bg-lilac-100'
      : '!border-indigo-200';
  }

  return (
    <li
      className={[
        'animate-fade-in transition-opacity',
        disabled ? 'w-0 overflow-hidden opacity-0' : '',
      ].join(' ')}
    >
      <Button
        className={[
          'animate-fade-in',
          'group p-reset inline-grid items-center w-8 h-8 text-sm font-sans rounded-full',
          buttonClassNames,
        ].join(' ')}
        disabled={disabled}
        filled={isActive && !isMinimal}
        label={label}
        href={href}
        size="small"
        variant={isMinimal ? 'light' : 'secondary'}
      />
    </li>
  );
};

const PaginationArrow: React.FC<{
  prev?: boolean;
}> = ({ prev = false }) => {
  const { translations } = useWordPressContext();

  return (
    <Image
      src="/arrow.svg"
      alt={prev ? translations['Previous'] : translations['Next']}
      height={12}
      width={15}
      className={[
        'grayscale group-hover:invert',
        prev ? 'rotate-180' : '',
      ].join(' ')}
    />
  );
};

const PaginationDots: React.FC = () => (
  <li className="animate-fade-in" aria-hidden="true">
    &#8230;
  </li>
);

const DOT_INDICATOR = 'DOTS';

type PagesRange = (typeof DOT_INDICATOR | number)[];

export type PaginationProps = {
  className?: string;
  limit: number;
  loading?: boolean;
  offset?: number;
  siblingCount?: number;
  updateUrls?: boolean;
  total: number;
  variant?: PaginationVariant;
  queryParamName?: string;
};

const getPagesRange = (start: number, end: number): number[] => {
  const length = end - start + 1;

  return Array.from({ length }, (_, idx) => idx + start);
};

function getPaginationUrl(page: number, queryParamName = 'page') {
  const { asPath, locale } = useRouter();
  const [path, queryParams] = asPath.split('?');
  const pathname = locale === 'en' ? path : `/${locale}${path}`;
  const searchParams = new URLSearchParams(queryParams);

  searchParams.delete(queryParamName);

  let paginationUrl = `${pathname}?${queryParamName}=${page.toString()}`;

  if (searchParams.size > 0) {
    paginationUrl += `&${searchParams.toString()}`;
  }

  return paginationUrl;
}

const Pagination: React.FC<PaginationProps> = ({
  className,
  limit,
  offset = 0,
  siblingCount = 1,
  total,
  variant = 'default',
  queryParamName = 'page',
}) => {
  const currentPage = Math.floor(offset / limit) + 1;
  const totalPages = Math.ceil(total / limit);
  const firstButton =
    currentPage - siblingCount > 1 ? currentPage - siblingCount : 1;
  const lastButton =
    currentPage + siblingCount < totalPages
      ? currentPage + siblingCount
      : totalPages;

  let pagesRange: PagesRange = getPagesRange(firstButton, lastButton);

  if (!pagesRange.includes(1)) {
    pagesRange = [
      1,
      1 < currentPage - siblingCount * 2 ? DOT_INDICATOR : null,
      ...pagesRange,
    ];
  }

  if (!pagesRange.includes(totalPages)) {
    pagesRange = [
      ...pagesRange,
      totalPages > siblingCount * 2 + currentPage ? DOT_INDICATOR : null,
      totalPages,
    ];
  }

  pagesRange = pagesRange.filter(Boolean);

  if (totalPages === 1) return null;

  return (
    <ul
      className={['grid grid-flow-col justify-start gap-x-2', className].join(
        ' ',
      )}
    >
      <PaginationButton
        disabled={currentPage <= 1}
        label={<PaginationArrow prev />}
        href={getPaginationUrl(currentPage - 1, queryParamName)}
        variant={variant}
      />
      {pagesRange.map((page, index) => {
        // If the pageItem is a DOT, render the DOTS unicode character
        if (page === DOT_INDICATOR) {
          return <PaginationDots key={index} />;
        }

        return (
          <PaginationButton
            key={`page-${index}`}
            label={page}
            isActive={currentPage === page}
            href={getPaginationUrl(page, queryParamName)}
            variant={variant}
          />
        );
      })}
      <PaginationButton
        disabled={currentPage >= totalPages}
        href={getPaginationUrl(currentPage + 1, queryParamName)}
        label={<PaginationArrow />}
        variant={variant}
      />
    </ul>
  );
};

export default Pagination;
